import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import ReactSelect from '../ReactSelect';
import { format, parseISO, startOfDay } from 'date-fns';
import useMode from '../../hooks/useMode';
import { PortalWithState } from 'react-portal';
import { DropdownIndicatorProps } from 'react-select';
import ReactSelectButton from '../ReactSelect/ReactSelectButton';
import CredentialEditModal from './CredentialEditModal';
import { CredentialType, Credential } from '../Tester/types';
import { gql as individual_gql } from '@/modes/individual/gql';
import { gql as company_gql } from '@/modes/company/gql';
import { gql as waterSupplier_gql } from '@/modes/waterSupplier/gql';

export const GET_CREDENTIALS_COMPANY = company_gql(`#graphql
  query GetCredentials($where: JSON, $orderBy: [ColumnOrder]) {
    vendors(where: $where, orderBy: $orderBy, limit: null) {
      id
      profileId
      profile(required: true) {
        id
        credentials(required: true, limit: null) {
          id
          city
          state
          value
          issueDate
          expireDate
          isDefault
          archived
        }
      }
    }
  }
`);

export const GET_CREDENTIALS_WATER_SUPPLIER = waterSupplier_gql(`#graphql
  query GetCredentials($where: JSON, $orderBy: [ColumnOrder]) {
    vendors(where: $where, orderBy: $orderBy, limit: null) {
      id
      profileId
      vendors(required: true, limit: null) {
        id
        profileId
        profile(required: true) {
          id
          credentials(required: true, limit: null) {
            id
            type
            city
            state
            value
            issueDate
            expireDate
            isDefault
            archived
          }
        }
      }
    }
  }
`);

export const GET_CREDENTIALS_INDIVIDUAL = individual_gql(`#graphql
  query GetCredentials($where: JSON, $orderBy: [ColumnOrder]) {
    me {
      id
      credentials(
        where: $where
        orderBy: $orderBy
        required: true
        limit: null
      ) {
        id
        city
        state
        value
        issueDate
        expireDate
        isDefault
        archived
      }
    }
  }
`);

export type CredentialPickerPickerProps = {
  testerProfileId: string;
  companyProfileId?: string;
  type: CredentialType;
  credentialId?: string | number;
  certificationNumber?: string | number;
  onChange: (credential: Credential | null) => void;
  placeholder?: string;
  allowCreate?: boolean;
};

const CredentialPicker = ({
  testerProfileId,
  companyProfileId,
  type,
  credentialId,
  certificationNumber,
  onChange,
  placeholder,
  allowCreate,
}: CredentialPickerPickerProps): JSX.Element => {
  const mode = useMode();

  const { data: dataIndividual, refetch: refetchIndividual } = useQuery(
    GET_CREDENTIALS_INDIVIDUAL,
    {
      fetchPolicy: 'network-only',
      variables: {
        where: { type },
      },
      skip: mode !== 'individual',
    }
  );

  const { data: dataCompany, refetch: refetchCompany } = useQuery(
    GET_CREDENTIALS_COMPANY,
    {
      fetchPolicy: 'network-only',
      variables: {
        where: {
          'profile.credentials.type': type,
          profileId: testerProfileId,
        },
        orderBy: [
          {
            col: 'profile.credentials.expireDate',
            dir: 'DESC',
          },
          {
            col: 'profile.credentials.issueDate',
            dir: 'DESC',
          },
        ],
      },
      skip: mode !== 'company',
    }
  );

  const { data: dataWaterSupplier, refetch: refetchWaterSupplier } = useQuery(
    GET_CREDENTIALS_WATER_SUPPLIER,
    {
      fetchPolicy: 'network-only',
      variables: {
        where: {
          profileId: companyProfileId,
          'vendors.profile.credentials.type': type,
          'vendors.profile.id': testerProfileId,
        },
        orderBy: [
          {
            col: 'vendors.profile.credentials.expireDate',
            dir: 'DESC',
          },
          {
            col: 'vendors.profile.credentials.issueDate',
            dir: 'DESC',
          },
        ],
      },
      skip: mode !== 'water-supplier' || !companyProfileId,
    }
  );

  const refetch =
    mode === 'individual'
      ? refetchIndividual
      : mode === 'company'
        ? refetchCompany
        : mode === 'water-supplier'
          ? refetchWaterSupplier
          : () => {};

  const credentials = (
    dataIndividual
      ? dataIndividual.me.credentials
      : dataCompany
        ? (dataCompany.vendors[0]?.profile.credentials ?? [])
        : dataWaterSupplier
          ? (dataWaterSupplier.vendors[0]?.vendors[0]?.profile.credentials ??
            [])
          : []
  ) as any[];

  const selectedCredential: Credential | undefined = useMemo(
    () =>
      credentials.find(
        (credential) =>
          credential.id == credentialId ||
          credential.value == certificationNumber
      ),
    [credentials, credentialId, certificationNumber]
  );

  const availableCredentials: Credential[] = useMemo(() => {
    if (credentials.length === 0) {
      return [];
    }
    return credentials.filter(
      (credential) =>
        !credential.archived &&
        (!credential.expireDate ||
          startOfDay(Date.now()).getTime() <=
            parseISO(credential.expireDate).getTime())
    );
  }, [credentials]);

  useEffect(() => {
    let credential: Credential | undefined = selectedCredential;

    if (
      (credentialId || certificationNumber) &&
      credentials.length > 0 &&
      !credential
    ) {
      console.warn(
        'Clearing not found credential',
        certificationNumber,
        credentialId
      );
      credential = undefined;
    }

    if (availableCredentials.length > 0 && !credential) {
      const defaultCredential = availableCredentials.find(
        (credential) => credential.isDefault
      );
      if (defaultCredential) {
        console.log('No credential selected. Using default credential');
        credential = defaultCredential;
      } else {
        console.log('No credential selected. Using first in list');
        credential = availableCredentials[0];
      }
      if (credential?.id !== (credentialId || undefined)) {
        setTimeout(() => {
          onChange(credential ?? null);
        });
      }
    }
  }, [
    credentialId,
    certificationNumber,
    selectedCredential,
    availableCredentials,
    credentials,
  ]);

  if (
    selectedCredential &&
    !availableCredentials.includes(selectedCredential)
  ) {
    availableCredentials.push(selectedCredential);
  }

  const assignCredential = async (credential: Credential) => {
    await refetch();
    onChange(credential);
  };

  return (
    <PortalWithState closeOnEsc>
      {({ openPortal, closePortal, portal, isOpen }) => {
        return (
          <>
            <ReactSelect
              value={selectedCredential}
              options={availableCredentials}
              noOptionsMessage={() => null}
              menuPlacement="auto"
              components={{
                DropdownIndicator: (
                  props: PropsWithChildren<
                    DropdownIndicatorProps<Credential, false>
                  >
                ) => {
                  return allowCreate ? (
                    <ReactSelectButton
                      onClick={openPortal}
                      title="Add new credential"
                    />
                  ) : null;
                },
              }}
              styles={{
                menu: (base) => ({
                  ...base,
                  minWidth: '250px',
                }),
              }}
              onChange={(selected: Credential | null) => {
                if (!selected) {
                  onChange(null);
                } else {
                  onChange(selected);
                }
              }}
              placeholder={placeholder ?? 'Select credential...'}
              getOptionLabel={(credential: Credential) => {
                const area = credential.city || credential.state;
                const issueDate = credential.issueDate
                  ? format(parseISO(credential.issueDate), 'M/d/yyyy')
                  : '';
                const expireDate = credential.expireDate
                  ? format(parseISO(credential.expireDate), 'M/d/yyyy')
                  : '';

                return `${area ? area + ' ' : ''}#${credential.value}${
                  credential.expireDate ? ` (exp ${expireDate})` : ''
                }`;
              }}
              getOptionValue={(credential: Credential) => credential.id}
            />
            {portal(
              <CredentialEditModal
                action="create"
                credential={{
                  profileId: testerProfileId,
                  type: 'BACKFLOW',
                }}
                onClose={closePortal}
                onCreate={assignCredential}
              />
            )}
          </>
        );
      }}
    </PortalWithState>
  );
};

export default CredentialPicker;
